/**
 * What the studio has changed here that has to be typed into Jackrabbit
 * (D-694, pilot item 1).
 *
 * `asked`: *"a report of changes we have made to classes or families etc that
 * she can run, so she can go back to Jackrabbit and make the updates there."*
 *
 * **A to-do list, not a log.** The server derives it from the ownership flags
 * the schema already carries, minus what has been ticked — so it is right the
 * first time it is ever run, and there is nothing to clear down.
 */

export type Carryover = {
  subject: string
  subjectId: string
  field: string
  /** The class or person's name — what to search for in Jackrabbit. */
  name: string
  /** What the studio says it is now, exactly as the tick will record it. */
  value: string
  /** Which Jackrabbit field to type it into, in her words. */
  where: string
  /**
   * What Jackrabbit said at the last import, or null where no import has seen
   * this field yet (D-749).
   *
   * **Null means "not seen yet", never "Jackrabbit holds nothing".** Nothing
   * was recorded before this existed, so a field fills in the first time an
   * import matches its class again. Drawn as an empty value it would read as
   * *they have none*, which would send her to type over something already
   * right — D-209 in the direction that costs money.
   */
  theirs: string | null
  /** When that import ran, so the left column can be dated. */
  theirsSeenAt: string | null
  /**
   * Whether *Use Jackrabbit's* is even a question on this row (D-815).
   *
   * **Answered by the server, never worked out here.** A class row carries a
   * Jackrabbit value like any other, so a screen-side `theirs !== value` put
   * the control on most of the real report, where every press was refused —
   * and *a link that refuses the person holding it is worse than no link*.
   * Which rows can be taken is a fact about what the app can write back, and
   * the browser does not have it.
   */
  canTakeTheirs: boolean
}

export type CarryoverList = { rows: Carryover[] }

export const CARRYOVER_KEY = ['carryover'] as const

export async function fetchCarryover(): Promise<CarryoverList> {
  const res = await fetch('/api/carryover')
  if (!res.ok) throw new Error(`Could not load what is still to carry over (${res.status})`)
  return res.json()
}

/**
 * Marks one row done.
 *
 * **The value goes back with it**, and that is the whole mechanism: the tick
 * records what she actually carried across, so if somebody edits the class
 * between the screen loading and her tapping, the row correctly stays
 * outstanding at its new value rather than being hidden at the old one.
 */
export async function markCarriedOver(row: Carryover): Promise<void> {
  const res = await fetch('/api/carryover/done', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      subject: row.subject,
      subjectId: row.subjectId,
      field: row.field,
      value: row.value,
    }),
  })

  if (!res.ok) throw new Error(`Could not mark that one done (${res.status})`)
}

/** What each kind of row is called on screen. */
export const FIELD_WORDS: Record<string, string> = {
  Schedule: 'Day, time and room',
  Capacity: 'Class size',
  AgeRange: 'Age range',
  Style: 'Style',
  LevelBand: 'Level',

  // Missing since D-603 added the field, so this row read "Cadence" on screen
  // until a cross-language test went looking. Found 2026-09-23.
  Cadence: 'How often it meets',

  MakeupCategory: 'What kind of class it is',
  Contact: 'Email and phone',

  /*
   * People (D-812). Each says whose it is, because the screen shows the
   * person's name and not which kind of person they are — and a parent and her
   * dancer share a surname, so "Email and phone" under "Webb" would not say
   * which Webb.
   */
  GuardianContact: "A parent's email and phone",

  // Missing for one commit after D-832 added the field, so the family-address
  // row was headed "HouseholdAddress" on the report Tonya works through. The
  // second time this exact omission has shipped - `Cadence` was the first
  // (D-603) - and the same cross-language test caught both.
  HouseholdAddress: "A family's address",

  DancerName: "A dancer's name",
  DancerDateOfBirth: "A dancer's date of birth",
  DancerStartDate: "A dancer's start date",

  Exists: 'Not in Jackrabbit at all',
}

/**
 * Takes Jackrabbit's value instead of ours, on one row (D-811).
 *
 * **It applies immediately** rather than releasing the claim and waiting for
 * the next import — `asked`, 2026-09-23. A control that does nothing visible
 * for a week is one nobody trusts the second time.
 */
export async function takeTheirs(row: Carryover): Promise<void> {
  const res = await fetch('/api/carryover/take-theirs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      subject: row.subject,
      subjectId: row.subjectId,
      field: row.field,
    }),
  })

  if (!res.ok) {
    /*
     * The server's sentence, not ours: every refusal here is something she can
     * act on — run an import, or there is nothing of theirs to take — and a
     * generic message would throw that away (D-209).
     */
    const said = await res.json().catch(() => null)
    throw new Error(said?.detail ?? `Could not take their value (${res.status})`)
  }
}

/**
 * **Whether Jackrabbit has already caught up.**
 *
 * The row stays listed either way — the filter is ownership, never a string
 * comparison, because a rendering difference deciding whether a row *exists*
 * is how one goes missing, and a missing row is a family billed for the wrong
 * class (D-694). The comparison is safe as a label and unsafe as a filter, so
 * this only changes what the row says.
 */
export function alreadyMatches(row: Carryover): boolean {
  return row.theirs !== null && row.theirs === row.value
}

/**
 * The heading for a row's change.
 *
 * Falls back to the server's own word rather than to nothing: a field this
 * screen has not been taught about is still a real job, and hiding its name
 * would leave a row she cannot identify (D-209).
 */
export function fieldName(field: string): string {
  return FIELD_WORDS[field] ?? field
}

/**
 * **Rows that need creating first, then rows that need editing.**
 *
 * They are different jobs: one is *add this in Jackrabbit*, the other is
 * *change this field*. Mixed together they make a list she cannot work through
 * in one pass — she would be switching between two screens in Jackrabbit on
 * every row.
 */
export function inWorkingOrder(rows: Carryover[]): Carryover[] {
  /*
   * Three bands now, not two. Rows where Jackrabbit has already caught up go
   * last: there is genuinely nothing to type, so they are a tick-and-forget
   * pass rather than work, and mixed in they make the list look longer than
   * the job is (D-811).
   */
  const rank = (r: Carryover) => (r.field === 'Exists' ? 0 : alreadyMatches(r) ? 2 : 1)

  return [...rows].sort(
    (a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name) || a.field.localeCompare(b.field),
  )
}
